/* eslint-env browser */
// Agent performance (wave 3, W2_Portal_Tables look): the period, agent and
// category as chips, the figures as tiles, three charts, the top performer,
// then every agent in the modern table ("Show details" lists their latest
// ten interactions).
//
// Data, as before: the call-centre remarks (fetchAllDriverRemarks) asked
// for when the page opens, bounded to the start of the previous month (the
// oldest preset), and released with stopFetchingAllDriverRemarks when it
// closes; a custom start before that floor widens the read once, as before.
// Everything else is computed here. Days, weeks and months are Addis Ababa
// time (owner rule), so the floor is the start of the previous Addis month.
// Call-centre feedback (29 Oct 2026): the filters fold away with "Hide
// filters" (kept per person in this browser) and leave one line with the
// period and the filters in use; a copy button beside each driver's number.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import { api } from 'common';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { tokens as c } from '../theme';
import DashboardCard from '../components/DashboardCard';
import { ADDIS_OFFSET_MS, formatAddisDate, formatAddisDateTime } from '../common/exportUtils';
import {
  ActiveFilters,
  AddisDateTimeField,
  ChipGroup,
  CopyButton,
  DataTable,
  DropChip,
  FieldLabel,
  FilterToggleButton,
  IconCircle,
  PageHeader,
  StatusChip,
  ALL,
  addisPartsToMs,
  formatPhoneDisplay,
  msToAddisParts,
  useFiltersOpen,
} from '../components/modern';

const CALL_CATEGORIES = ['Compliance', 'Booking', 'Credit', 'SOS', 'Complain', 'Other'];

// One colour per call category, from the theme (charts and dots).
const CATEGORY_COLOR = {
  Compliance: c.INFO_TEXT,
  Booking: c.SUCCESS_TEXT,
  Credit: c.WARNING_TEXT,
  SOS: c.DANGER_TEXT,
  Complain: c.ACCENT,
  Other: c.TEXT_3,
};
const getCategoryColor = (category) => CATEGORY_COLOR[category] || c.TEXT_3;
const CATEGORY_TONE = { Compliance: 'info', Booking: 'success', Credit: 'warning', SOS: 'error', Complain: 'accent', Other: 'neutral' };

const DAY_MS = 24 * 60 * 60 * 1000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const NUMBER_FORMAT = new Intl.NumberFormat('en-US');
const fmt = (n) => NUMBER_FORMAT.format(n || 0);

// Addis Ababa calendar helpers (instants in ms).
const dayStart = (ms) => {
  const p = msToAddisParts(ms);
  return addisPartsToMs({ y: p.y, m: p.m, d: p.d, h: 0, mi: 0 });
};
const weekStart = (ms) => {
  const start = dayStart(ms);
  const weekday = new Date(start + ADDIS_OFFSET_MS).getUTCDay(); // 0 = Sunday
  return start - weekday * DAY_MS;
};
const monthStart = (ms, offset = 0) => {
  const p = msToAddisParts(ms);
  const index = p.y * 12 + (p.m - 1) + offset;
  return addisPartsToMs({ y: Math.floor(index / 12), m: (index % 12) + 1, d: 1, h: 0, mi: 0 });
};

const presetRange = (preset, now) => {
  switch (preset) {
    case 'yesterday':
      return { start: dayStart(now) - DAY_MS, end: dayStart(now) - 1 };
    case 'this_week':
      return { start: weekStart(now), end: weekStart(now) + 7 * DAY_MS - 1 };
    case 'last_week':
      return { start: weekStart(now) - 7 * DAY_MS, end: weekStart(now) - 1 };
    case 'this_month':
      return { start: monthStart(now), end: monthStart(now, 1) - 1 };
    case 'last_month':
      return { start: monthStart(now, -1), end: monthStart(now) - 1 };
    case 'today':
    default:
      return { start: dayStart(now), end: dayStart(now) + DAY_MS - 1 };
  }
};

export default function AgentPerformanceDashboard() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const remarksData = useSelector((state) => state.driverremarksdata);
  const settings = useSelector((state) => state.settingsdata.settings);

  const { fetchAllDriverRemarks, stopFetchingAllDriverRemarks } = api;

  const [dateFilter, setDateFilter] = useState('today');
  const [range, setRange] = useState(() => presetRange('today', Date.now()));
  const [selectedAgent, setSelectedAgent] = useState(ALL);
  const [selectedCategory, setSelectedCategory] = useState(ALL);
  // The filter card folds away (this person's choice, kept in this browser).
  const [filtersOpen, toggleFilters] = useFiltersOpen('agent_performance');
  const startMs = range.start;
  const endMs = range.end;

  // Fetch remarks on mount, bounded to the start of the previous month (the
  // oldest preset) instead of the whole /driver_remarks history. The fetched
  // floor is tracked so a custom range reaching further back widens the read.
  const remarksFloorRef = useRef(new Date(monthStart(Date.now(), -1)).toISOString());
  useEffect(() => {
    dispatch(fetchAllDriverRemarks(remarksFloorRef.current));
    return () => {
      dispatch(stopFetchingAllDriverRemarks());
    };
  }, [dispatch, fetchAllDriverRemarks, stopFetchingAllDriverRemarks]);

  // Custom start date below the fetched floor → re-fetch from that date.
  useEffect(() => {
    if (!Number.isFinite(startMs)) return;
    const startIso = new Date(dayStart(startMs)).toISOString();
    if (startIso < remarksFloorRef.current) {
      remarksFloorRef.current = startIso;
      dispatch(fetchAllDriverRemarks(startIso));
    }
  }, [startMs, dispatch, fetchAllDriverRemarks]);

  // Presets set the range; "custom" keeps it and shows the two date fields.
  const choosePreset = (value) => {
    setDateFilter(value);
    if (value !== 'custom') setRange(presetRange(value, Date.now()));
  };

  // Filter remarks and calculate agent performance — derived data
  const { filteredRemarks, agentPerformanceData } = useMemo(() => {
    if (!remarksData?.allRemarks) {
      return { filteredRemarks: [], agentPerformanceData: [] };
    }

    const startTs = startMs;
    const endTs = endMs;

    let filtered = remarksData.allRemarks.filter((remark) => {
      const ts = new Date(remark.timestamp).getTime();
      return ts >= startTs && ts <= endTs;
    });

    if (selectedCategory !== ALL) {
      filtered = filtered.filter((remark) => remark.callType === selectedCategory);
    }
    if (selectedAgent !== ALL) {
      filtered = filtered.filter((remark) => remark.agentId === selectedAgent);
    }

    const agentMap = {};
    filtered.forEach((remark) => {
      const agentId = remark.agentId;
      if (!agentMap[agentId]) {
        agentMap[agentId] = {
          agentId,
          agentEmail: remark.agentEmail || t('unknown'),
          agentName: remark.agentName || t('unknown'),
          totalCalls: 0,
          categories: {},
          uniqueDrivers: new Set(),
          remarks: [],
        };
      }
      agentMap[agentId].totalCalls++;
      agentMap[agentId].categories[remark.callType] = (agentMap[agentId].categories[remark.callType] || 0) + 1;
      agentMap[agentId].uniqueDrivers.add(remark.driverId);
      agentMap[agentId].remarks.push(remark);
    });

    const days = Math.floor((endTs - startTs) / DAY_MS) + 1;
    const agentData = Object.values(agentMap)
      .map((agent) => ({
        ...agent,
        uniqueDriversCount: agent.uniqueDrivers.size,
        avgCallsPerDay: agent.remarks.length > 0 ? (agent.remarks.length / days).toFixed(1) : 0,
        complianceCalls: agent.categories['Compliance'] || 0,
        bookingCalls: agent.categories['Booking'] || 0,
        creditCalls: agent.categories['Credit'] || 0,
        sosCalls: agent.categories['SOS'] || 0,
        complainCalls: agent.categories['Complain'] || 0,
        otherCalls: agent.categories['Other'] || 0,
      }))
      .sort((a, b) => b.totalCalls - a.totalCalls);

    return { filteredRemarks: filtered, agentPerformanceData: agentData };
  }, [remarksData?.allRemarks, startMs, endMs, selectedAgent, selectedCategory, t]);

  // Get unique agents for filter dropdown
  const uniqueAgents = useMemo(() => {
    if (!remarksData?.allRemarks) return [];
    const agents = {};
    remarksData.allRemarks.forEach((remark) => {
      if (remark.agentId && !agents[remark.agentId]) {
        agents[remark.agentId] = {
          id: remark.agentId,
          email: remark.agentEmail,
          name: remark.agentName,
        };
      }
    });
    return Object.values(agents);
  }, [remarksData?.allRemarks]);

  // Summary statistics
  const summaryStats = useMemo(() => {
    const totalCalls = filteredRemarks.length;
    const totalAgents = agentPerformanceData.length;
    const uniqueDrivers = new Set(filteredRemarks.map((r) => r.driverId)).size;

    const categoryBreakdown = {};
    CALL_CATEGORIES.forEach((cat) => {
      categoryBreakdown[cat] = filteredRemarks.filter((r) => r.callType === cat).length;
    });

    const topAgent = agentPerformanceData[0] || null;

    return {
      totalCalls,
      totalAgents,
      uniqueDrivers,
      categoryBreakdown,
      topAgent,
      avgCallsPerAgent: totalAgents > 0 ? (totalCalls / totalAgents).toFixed(1) : 0,
    };
  }, [filteredRemarks, agentPerformanceData]);

  // Chart data for category distribution
  const categoryChartData = useMemo(() => {
    return CALL_CATEGORIES.map((cat) => ({
      name: t(cat.toLowerCase()),
      value: summaryStats.categoryBreakdown[cat] || 0,
      color: getCategoryColor(cat),
    })).filter((item) => item.value > 0);
  }, [summaryStats.categoryBreakdown, t]);

  // Chart data for agent comparison
  const agentComparisonData = useMemo(() => {
    return agentPerformanceData.slice(0, 10).map((agent) => ({
      name: agent.agentEmail?.split('@')[0] || t('unknown'),
      calls: agent.totalCalls,
      drivers: agent.uniqueDriversCount,
    }));
  }, [agentPerformanceData, t]);

  // Daily trend data (one point per Addis Ababa day)
  const dailyTrendData = useMemo(() => {
    if (!filteredRemarks.length) return [];

    const dailyMap = {};
    filteredRemarks.forEach((remark) => {
      const ts = new Date(remark.timestamp).getTime();
      if (!Number.isFinite(ts)) return;
      const day = dayStart(ts);
      dailyMap[day] = (dailyMap[day] || 0) + 1;
    });

    return Object.keys(dailyMap)
      .map(Number)
      .sort((a, b) => a - b)
      .map((day) => {
        const p = msToAddisParts(day);
        return { date: `${p.d} ${MONTHS[p.m - 1]}`, calls: dailyMap[day] };
      });
  }, [filteredRemarks]);

  const allowPrivate = !!(settings && settings.AllowCriticalEditsAdmin);
  const hidden = t('hidden_demo');
  const phoneWord = t('phone', { defaultValue: 'Phone number' });
  const categoryLabel = (cat) => (cat ? t(String(cat).toLowerCase(), { defaultValue: cat }) : '');
  const numberCol = (id, title, field) => ({ id, title, field, type: 'number' });

  const columns = [
    {
      id: 'agent',
      title: t('agent'),
      field: 'agentEmail',
      searchValue: (row) => `${row.agentName || ''} ${row.agentEmail || ''}`,
      render: (rowData) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 200 }}>
          <IconCircle icon={<SupportAgentOutlinedIcon />} hue="info" size={32} />
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{rowData.agentName}</Box>
            <Box sx={{ fontSize: 12.5, color: c.TEXT_3, overflowWrap: 'anywhere' }}>{rowData.agentEmail}</Box>
          </Box>
        </Box>
      ),
    },
    {
      ...numberCol('totalCalls', t('total_calls'), 'totalCalls'),
      defaultSort: 'desc',
      render: (rowData) => (
        <Box component="span" sx={{ fontWeight: 700 }}>
          {fmt(rowData.totalCalls)}
        </Box>
      ),
    },
    numberCol('uniqueDriversCount', t('unique_drivers'), 'uniqueDriversCount'),
    { id: 'avgCallsPerDay', title: t('avg_calls_per_day'), field: 'avgCallsPerDay', type: 'number', sortValue: (row) => Number(row.avgCallsPerDay) },
    numberCol('complianceCalls', t('compliance'), 'complianceCalls'),
    numberCol('bookingCalls', t('booking'), 'bookingCalls'),
    numberCol('creditCalls', t('credit'), 'creditCalls'),
    numberCol('sosCalls', t('sos'), 'sosCalls'),
    numberCol('complainCalls', t('complain'), 'complainCalls'),
  ];

  // The export: the agent's name and email, then the same figures.
  const exportColumns = [
    { title: t('agent'), field: 'agentName' },
    { title: t('email', { defaultValue: 'Email' }), field: 'agentEmail' },
    { title: t('total_calls'), field: 'totalCalls', align: 'right' },
    { title: t('unique_drivers'), field: 'uniqueDriversCount', align: 'right' },
    { title: t('avg_calls_per_day'), field: 'avgCallsPerDay', align: 'right' },
    { title: t('compliance'), field: 'complianceCalls', align: 'right' },
    { title: t('booking'), field: 'bookingCalls', align: 'right' },
    { title: t('credit'), field: 'creditCalls', align: 'right' },
    { title: t('sos'), field: 'sosCalls', align: 'right' },
    { title: t('complain'), field: 'complainCalls', align: 'right' },
  ];

  const headSx = { bgcolor: c.SURFACE_ALT, color: c.INK_2, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', py: 1 };
  const detailPanel = (rowData) => (
    <Box sx={{ px: { xs: 1, md: 2 } }}>
      <Typography component="h3" sx={{ fontSize: 15, fontWeight: 700, color: c.INK, mb: 1 }}>
        {`${t('recent_interactions')} — ${rowData.agentName}`}
      </Typography>
      <TableContainer sx={{ border: `1px solid ${c.OUTLINE}`, borderRadius: '10px', bgcolor: c.SURFACE }}>
        <Table size="small" aria-label={`${t('recent_interactions')}: ${rowData.agentName}`} sx={{ minWidth: 720, '& td, & th': { fontSize: 13.5, color: c.INK } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>{t('date')}</TableCell>
              <TableCell sx={headSx}>{t('driver')}</TableCell>
              <TableCell sx={headSx}>{t('category', { defaultValue: 'Category' })}</TableCell>
              <TableCell sx={headSx}>{t('remark')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rowData.remarks.slice(0, 10).map((remark, idx) => (
              <TableRow key={remark.id || idx}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatAddisDateTime(remark.timestamp)}</TableCell>
                <TableCell>
                  <Box sx={{ fontWeight: 600 }}>{remark.driverName}</Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, fontSize: 12.5, color: c.TEXT_3, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {allowPrivate ? formatPhoneDisplay(remark.driverMobile) : hidden}
                    {allowPrivate ? <CopyButton value={remark.driverMobile} phone what={phoneWord} sx={{ my: -0.5 }} /> : null}
                  </Box>
                </TableCell>
                <TableCell>
                  <StatusChip tone={CATEGORY_TONE[remark.callType] || 'neutral'} label={categoryLabel(remark.callType)} />
                </TableCell>
                <TableCell>
                  <Tooltip title={remark.remark || ''}>
                    <Box component="span" tabIndex={0} sx={{ display: 'inline-block', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                      {remark.remark}
                    </Box>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {rowData.remarks.length > 10 ? (
        <Typography sx={{ mt: 1, fontSize: 13, color: c.TEXT_3 }}>{`${t('showing_first_10_of')} ${rowData.remarks.length} ${t('interactions')}`}</Typography>
      ) : null}
    </Box>
  );

  const periodOptions = [
    { value: 'today', label: t('today') },
    { value: 'yesterday', label: t('yesterday', { defaultValue: 'Yesterday' }) },
    { value: 'this_week', label: t('this_week') },
    { value: 'last_week', label: t('last_week') },
    { value: 'this_month', label: t('this_month') },
    { value: 'last_month', label: t('last_month') },
    { value: 'custom', label: t('custom') },
  ];
  const agentOptions = [{ value: ALL, label: t('all_agents') }].concat(
    uniqueAgents.map((agent) => ({ value: agent.id, label: agent.email?.split('@')[0] || agent.name || t('unknown') }))
  );
  const categoryOptions = [{ value: ALL, label: t('all_categories') }].concat(CALL_CATEGORIES.map((cat) => ({ value: cat, label: categoryLabel(cat) })));
  const rangeText = `${formatAddisDate(startMs)} – ${formatAddisDate(endMs)}`;

  // The filters in use (today is the default period), shown on one line
  // while the filter card is folded: they stay applied.
  const labelOf = (options, value) => (options.find((o) => o.value === value) || { label: String(value) }).label;
  const filterTokens = [];
  if (dateFilter !== 'today') filterTokens.push({ key: 'period', group: t('date_range'), value: labelOf(periodOptions, dateFilter) });
  if (selectedAgent !== ALL) filterTokens.push({ key: 'agent', group: t('agent'), value: labelOf(agentOptions, selectedAgent) });
  if (selectedCategory !== ALL) filterTokens.push({ key: 'category', group: t('category', { defaultValue: 'Category' }), value: categoryLabel(selectedCategory) });
  const removeToken = (key) => {
    if (key === 'period') choosePreset('today');
    if (key === 'agent') setSelectedAgent(ALL);
    if (key === 'category') setSelectedCategory(ALL);
  };
  const clearFilters = () => {
    choosePreset('today');
    setSelectedAgent(ALL);
    setSelectedCategory(ALL);
  };
  const loading = !!remarksData?.allRemarksLoading && !remarksData?.allRemarks;
  const title = t('agent_performance_details');
  const empty = <Typography sx={{ color: c.TEXT_3, fontSize: 14 }}>{t('no_data_available')}</Typography>;
  const chartBox = { height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' };
  const axisTick = { fontSize: 11, fill: c.INK_2 };

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader count={t('agent_performance_description')}>
        <FilterToggleButton open={filtersOpen} onClick={toggleFilters} activeCount={filterTokens.length} controls="agent-perf-filters" />
      </PageHeader>

      {/* Filters: the card folds away; folded, one line keeps the period and the filters in use. */}
      <Box>
        <Collapse in={filtersOpen} id="agent-perf-filters">
          <Paper sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <ChipGroup label={t('date_range')} value={dateFilter} options={periodOptions} onChange={choosePreset} />
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
              <DropChip label={t('agent')} value={selectedAgent} options={agentOptions} onChange={setSelectedAgent} />
              <DropChip label={t('category', { defaultValue: 'Category' })} value={selectedCategory} options={categoryOptions} onChange={setSelectedCategory} />
              <Typography component="span" sx={{ fontSize: 14, color: c.INK_2, fontVariantNumeric: 'tabular-nums' }}>
                {rangeText}
              </Typography>
            </Box>
            {dateFilter === 'custom' ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <FieldLabel htmlFor="agent-perf-start">{t('start_date')}</FieldLabel>
                  <AddisDateTimeField
                    id="agent-perf-start"
                    dateOnly
                    value={startMs}
                    onChange={(ms) => setRange((prev) => ({ ...prev, start: dayStart(ms) }))}
                  />
                </Box>
                <Box>
                  <FieldLabel htmlFor="agent-perf-end">{t('end_date')}</FieldLabel>
                  <AddisDateTimeField
                    id="agent-perf-end"
                    dateOnly
                    value={dayStart(endMs)}
                    onChange={(ms) => setRange((prev) => ({ ...prev, end: dayStart(ms) + DAY_MS - 1 }))}
                  />
                </Box>
              </Box>
            ) : null}
          </Paper>
        </Collapse>
        {!filtersOpen ? (
          <Paper sx={{ px: { xs: 2, md: 2.5 }, py: 1.5 }}>
            <ActiveFilters
              countText={rangeText}
              tokens={filterTokens}
              onRemove={removeToken}
              onClearAll={filterTokens.length > 1 ? clearFilters : undefined}
            />
          </Paper>
        ) : null}
      </Box>

      {/* Summary tiles */}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
        <DashboardCard icon={<PhoneInTalkOutlinedIcon />} hue="accent" title={t('total_interactions')} value={fmt(summaryStats.totalCalls)} loading={loading} />
        <DashboardCard icon={<SupportAgentOutlinedIcon />} hue="success" title={t('active_agents')} value={fmt(summaryStats.totalAgents)} loading={loading} />
        <DashboardCard icon={<PersonOutlineRoundedIcon />} hue="amber" title={t('drivers_contacted')} value={fmt(summaryStats.uniqueDrivers)} loading={loading} />
        <DashboardCard icon={<TrendingUpRoundedIcon />} hue="info" title={t('avg_per_agent')} value={String(summaryStats.avgCallsPerAgent)} loading={loading} />
      </Box>

      {/* Charts */}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(3, minmax(0, 1fr))' } }}>
        <Paper component="section" aria-label={t('category_distribution')} sx={{ p: 2, minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: 16, fontWeight: 700, color: c.INK, mb: 1 }}>
            {t('category_distribution')}
          </Typography>
          {categoryChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={categoryChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {categoryChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={chartBox}>{empty}</Box>
          )}
        </Paper>

        <Paper component="section" aria-label={t('agent_comparison')} sx={{ p: 2, minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: 16, fontWeight: 700, color: c.INK, mb: 1 }}>
            {t('agent_comparison')}
          </Typography>
          {agentComparisonData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={agentComparisonData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={c.OUTLINE} />
                <XAxis type="number" tick={axisTick} />
                <YAxis dataKey="name" type="category" width={80} tick={axisTick} />
                <RechartsTooltip />
                <Bar dataKey="calls" fill={c.ACCENT} name={t('calls')} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={chartBox}>{empty}</Box>
          )}
        </Paper>

        <Paper component="section" aria-label={t('daily_trend')} sx={{ p: 2, minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: 16, fontWeight: 700, color: c.INK, mb: 1 }}>
            {t('daily_trend')}
          </Typography>
          {dailyTrendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={dailyTrendData}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.OUTLINE} />
                <XAxis dataKey="date" tick={axisTick} />
                <YAxis tick={axisTick} allowDecimals={false} />
                <RechartsTooltip />
                <Line type="monotone" dataKey="calls" stroke={c.ACCENT} strokeWidth={2} dot={{ fill: c.ACCENT }} name={t('calls')} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={chartBox}>{empty}</Box>
          )}
        </Paper>
      </Box>

      {/* Top performer */}
      {summaryStats.topAgent ? (
        <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: c.AMBER_TINT, borderColor: c.AMBER_TINT }}>
          <IconCircle icon={<EmojiEventsOutlinedIcon />} hue="amber" size={48} sx={{ bgcolor: c.WHITE }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h2" sx={{ fontSize: 16, fontWeight: 700, color: c.INK, overflowWrap: 'anywhere' }}>
              {`${t('top_performer')}: ${summaryStats.topAgent.agentName}`}
            </Typography>
            <Typography sx={{ fontSize: 14, color: c.INK_2, fontVariantNumeric: 'tabular-nums' }}>
              {`${summaryStats.topAgent.totalCalls} ${t('calls')} · ${summaryStats.topAgent.uniqueDriversCount} ${t('unique_drivers')} · ${summaryStats.topAgent.avgCallsPerDay} ${t('avg_per_day')}`}
            </Typography>
          </Box>
        </Paper>
      ) : null}

      {/* Agent performance table */}
      <DataTable
        title={title}
        rows={agentPerformanceData}
        columns={columns}
        rowKey="agentId"
        loading={loading}
        countText={({ shown, total, query, loading: busy }) =>
          busy
            ? t('loading', { defaultValue: 'Loading…' })
            : query
              ? t('table_count_shown', { shown: fmt(shown), total: fmt(total), defaultValue: '{{shown}} of {{total}}' })
              : t('agent_perf_count', {
                  count: total,
                  n: fmt(total),
                  defaultValue_one: '{{n}} agent',
                  defaultValue_other: '{{n}} agents',
                  defaultValue: '{{n}} agents',
                })
        }
        subtitle={rangeText}
        rowLabel={(row) => row.agentName}
        detailPanel={detailPanel}
        prefsKey="agent_performance"
        exportCsv
        exportPrint
        exportRows="all"
        exportColumns={exportColumns}
        exportFileName="agent_performance"
        exportTitle={title}
        exportSubtitle={rangeText}
        minWidth={1100}
        emptyState={({ query }) => (query ? null : { icon: <QueryStatsOutlinedIcon />, title: t('agent_perf_empty', { defaultValue: 'No agent calls in this period' }) })}
      />
    </Box>
  );
}
